import { useState } from 'react';
import type { FormEvent } from 'react';
import { comments, fallbackComments } from '@/mocks/gossip';
import type { CommentItem } from '@/mocks/gossip';

const MAX_LEN = 500;

const avatarBg = ['bg-primary-100 text-primary-700', 'bg-secondary-100 text-secondary-900', 'bg-accent-100 text-accent-900'];

export default function CommentSection({ topicId }: { topicId: string }) {
  const seeded = comments.filter((c) => c.topicId === topicId);
  const [list, setList] = useState<CommentItem[]>(seeded.length ? seeded : fallbackComments);
  const [nick, setNick] = useState('');
  const [text, setText] = useState('');
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [feedback, setFeedback] = useState('');

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const value = text.trim();
    if (!value) {
      setFeedback('先写点什么再发布吧。');
      return;
    }
    if (value.length > MAX_LEN) {
      setFeedback(`内容不能超过 ${MAX_LEN} 字。`);
      return;
    }
    const next: CommentItem = {
      id: `local-${Date.now()}`,
      topicId,
      nick: nick.trim() || '匿名瓜友',
      badge: '今日已完成打卡',
      text: value,
      time: '刚刚',
      likes: 0,
    };
    setList((prev) => [next, ...prev]);
    setText('');
    setNick('');
    setFeedback('发布成功，今日打卡已完成。');
  };

  const toggleLike = (id: string) => {
    setLiked((prev) => ({ ...prev, [id]: !prev[id] }));
    setList((prev) =>
      prev.map((c) => (c.id === id ? { ...c, likes: c.likes + (liked[id] ? -1 : 1) } : c)),
    );
  };

  return (
    <section id="comments" className="mt-10">
      <div className="flex items-center justify-between gap-3 mb-5">
        <h2 className="font-heading text-xl md:text-2xl text-foreground-950 flex items-center gap-2">
          <i className="ri-chat-smile-2-line text-primary-500"></i>
          评论区打卡（{list.length}）
        </h2>
        <span className="text-xs text-foreground-500 hidden sm:block">留言即完成当日打卡</span>
      </div>

      <form onSubmit={handleSubmit} className="rounded-lg bg-background-100 border border-background-200 p-4 md:p-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="sm:col-span-1">
            <span className="block text-xs text-foreground-600 mb-1.5">昵称（可选）</span>
            <input
              type="text"
              name="nickname"
              value={nick}
              onChange={(e) => setNick(e.target.value)}
              maxLength={20}
              placeholder="匿名瓜友"
              className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
            />
          </label>
          <label className="sm:col-span-2">
            <span className="block text-xs text-foreground-600 mb-1.5">说点什么</span>
            <textarea
              name="content"
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
              rows={2}
              placeholder="分享你的看法，和其他瓜友一起聊聊"
              className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm resize-none focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
            ></textarea>
          </label>
        </div>
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span className="text-xs text-foreground-500">
            {text.length}/{MAX_LEN} 字
          </span>
          <div className="flex items-center gap-3">
            {feedback && <span className="text-xs text-secondary-700">{feedback}</span>}
            <button
              type="submit"
              className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              发布并打卡
            </button>
          </div>
        </div>
      </form>

      <ul className="mt-6 space-y-4">
        {list.map((item, index) => (
          <li key={item.id} className="flex gap-3 md:gap-4">
            <span
              className={`shrink-0 w-10 h-10 flex items-center justify-center rounded-full text-sm ${
                avatarBg[index % avatarBg.length]
              }`}
            >
              {item.nick.slice(0, 1)}
            </span>
            <div className="flex-1 min-w-0 pb-4 border-b border-background-200">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-900">{item.nick}</span>
                <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                  {item.badge}
                </span>
                <span className="text-xs text-foreground-400">{item.time}</span>
              </div>
              <p className="mt-2 text-sm text-foreground-700 leading-relaxed">{item.text}</p>
              <button
                type="button"
                onClick={() => toggleLike(item.id)}
                className={`mt-2 inline-flex items-center gap-1.5 text-xs transition-colors cursor-pointer ${
                  liked[item.id] ? 'text-primary-600' : 'text-foreground-500 hover:text-primary-600'
                }`}
              >
                <i className={liked[item.id] ? 'ri-heart-3-fill' : 'ri-heart-3-line'}></i>
                {item.likes}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}